/**
 * Utility functions to match cards to game sets/expansions across multiple TCGs
 */

export interface GameSet {
  id: number;
  game_id: number;
  code: string;
  name: string;
  slug?: string;
  external_id?: string;
  release_date?: string;
  icon_url?: string;
  cards_included?: number;
  tourney_restrict?: boolean;
  temp_type?: string;
  maintenance?: boolean;
}

export interface UserCardSummary {
  id: string | number;
  user_card_id?: string | number;
  card_id: string | number;
  name: string;
  imageUrl?: string;
  image_url?: string;
  external_id?: string;
  code?: string;
  game_id?: number;
  quantity: number;
  raridade?: string;
}

/**
 * Matches a card to its corresponding set from a list of sets of that game
 */
export function matchCardToSet(card: any, sets: GameSet[]): GameSet | null {
  if (!card || !sets || sets.length === 0) return null;

  const cardCode = (card.code || '').trim().toUpperCase();
  const extId = (card.external_id || '').trim().toUpperCase();
  const cardSetCode = (card.set_code || '').trim().toUpperCase();
  const variantCode = (card.variant_code || card.variantCode || '').trim().toUpperCase();
  const identifiers = Array.from(new Set([cardCode, extId, variantCode].filter(Boolean)));
  const cardName = (card.name || '').trim().toUpperCase();

  // 0. Direct match by explicit card.set_code
  if (cardSetCode) {
    const directSet = sets.find(s => (s.code || '').trim().toUpperCase() === cardSetCode);
    if (directSet) return directSet;
  }

  // 0.1 Direct match by prefix or suffix around separator (e.g. "SUDA-EN001" -> "SUDA", "010198_SWSH06" -> "SWSH06")
  for (const idStr of identifiers) {
    if (idStr.includes('-') || idStr.includes('_')) {
      const parts = idStr.split(/[-_]/);
      const prefix = parts[0].trim().toUpperCase();
      const suffix = parts[parts.length - 1].trim().toUpperCase();
      if (prefix && prefix.length <= 15) {
        const setByPrefix = sets.find(s => (s.code || '').trim().toUpperCase() === prefix);
        if (setByPrefix) return setByPrefix;
      }
      if (suffix && suffix.length <= 15) {
        const setBySuffix = sets.find(s => (s.code || '').trim().toUpperCase() === suffix);
        if (setBySuffix) return setBySuffix;
      }
    }
  }

  // 1. Direct code prefix/suffix with separator:
  // e.g. "OP06-007" -> "OP06", "ST12-09" -> "ST12", "BT14-101" -> "BT14", "6998_AOR" -> "AOR", "FB01-001" -> "FB01"
  for (const s of sets) {
    const sCode = (s.code || '').trim().toUpperCase();
    if (!sCode) continue;

    for (const idStr of identifiers) {
      if (
        idStr.startsWith(sCode + '-') ||
        idStr.startsWith(sCode + '_') ||
        idStr.startsWith(sCode + ' ') ||
        idStr.startsWith(sCode + '/') ||
        idStr.endsWith('_' + sCode) ||
        idStr.endsWith('-' + sCode)
      ) {
        return s;
      }
      if (idStr === sCode) {
        return s;
      }
    }
  }

  // 2. Slash format with set card count (e.g. Pokémon: "95/181", "024/149", "14/131", "30/30", "14/34"):
  for (const idStr of identifiers) {
    const slashMatch = idStr.match(/(\d+)[a-z]?\s*\/\s*(\d+)/i) || cardName.match(/(\d+)[a-z]?\s*\/\s*(\d+)/i);
    if (slashMatch) {
      const totalInCard = parseInt(slashMatch[2], 10);
      const matchingSets = sets.filter(s => s.cards_included === totalInCard);
      if (matchingSets.length === 1) {
        return matchingSets[0];
      } else if (matchingSets.length > 1) {
        // Tie break with card name or set code
        for (const s of matchingSets) {
          const sCode = (s.code || '').toUpperCase();
          const sName = (s.name || '').toUpperCase();
          if (sCode && (cardName.includes(sCode) || idStr.includes(sCode))) return s;
          if (sName && (cardName.includes(sName) || idStr.includes(sName))) return s;
        }
        return matchingSets[0];
      }
    }
  }

  // 3. Promo codes (e.g. "SM194", "SWSH050", "SVP001", "P-001"):
  for (const s of sets) {
    const sCode = (s.code || '').trim().toUpperCase();
    if (sCode && sCode.length >= 2) {
      // Check if idStr starts with sCode followed by numbers
      for (const idStr of identifiers) {
        if (new RegExp('^' + sCode + '\\d+').test(idStr)) {
          return s;
        }
      }
      if (new RegExp('[- ]' + sCode + '\\d+').test(cardName)) {
        return s;
      }
    }
  }

  // 4. Substring in card name or external_id (e.g. "Detective Pikachu - SM194" or "Alolan Vulpix - Sun & Moon")
  for (const s of sets) {
    const sCode = (s.code || '').trim().toUpperCase();
    const sName = (s.name || '').trim().toUpperCase();
    for (const idStr of identifiers) {
      if (sCode && sCode.length >= 3 && (idStr.includes(sCode) || cardName.includes(sCode))) {
        return s;
      }
    }
    if (sName && sName.length >= 4 && cardName.includes(sName)) {
      return s;
    }
  }

  return null;
}

export interface SetWithStats extends GameSet {
  userCards: UserCardSummary[];
  uniqueOwnedCount: number;
  totalCopiesCount: number;
  completionRate: number; // 0 to 100
  isComplete: boolean;
}

/**
 * Computes user collection statistics for each set
 */
export function aggregateSetsWithUserCards(
  sets: GameSet[],
  userCards: UserCardSummary[]
): SetWithStats[] {
  // Map of setId -> array of cards
  const setCardsMap = new Map<number, UserCardSummary[]>();
  sets.forEach(s => setCardsMap.set(s.id, []));

  userCards.forEach(card => {
    const matchedSet = matchCardToSet(card, sets);
    if (matchedSet) {
      const existing = setCardsMap.get(matchedSet.id);
      if (existing) {
        existing.push(card);
      } else {
        setCardsMap.set(matchedSet.id, [card]);
      }
    }
  });

  return sets.map(s => {
    const cards = setCardsMap.get(s.id) || [];
    // Count unique cards by card_id or external_id
    const uniqueIds = new Set<string>();
    let totalCopies = 0;

    cards.forEach(c => {
      const key = String(c.external_id || c.card_id || c.id || c.name);
      uniqueIds.add(key);
      totalCopies += typeof c.quantity === 'number' ? c.quantity : 1;
    });

    const uniqueOwnedCount = uniqueIds.size;
    const totalInSet = s.cards_included || 0;
    const completionRate =
      totalInSet > 0
        ? Math.min(100, Math.round((uniqueOwnedCount / totalInSet) * 1000) / 10)
        : 0;

    const isComplete = totalInSet > 0 && uniqueOwnedCount >= totalInSet;

    return {
      ...s,
      userCards: cards,
      uniqueOwnedCount,
      totalCopiesCount: totalCopies,
      completionRate,
      isComplete,
    };
  });
}
